import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTranslation } from 'react-i18next';

import ListFilterToolbar from 'src/common/components/list-filters/ListFilterToolbar';
import FilterFieldHeader from 'src/common/components/list-filters/FilterFieldHeader';
import { NAME, SER } from 'src/common/constants/participantSortKeys';
import { hasActiveListFilter } from 'src/helpers/listFilters';
import { ECheckinFilters } from 'src/interfaces/Checkin';
import { EOrder } from 'src/interfaces/Order';
import { EView } from 'src/interfaces/View';

interface Props {
  filter: string;
  onChange: (key: ECheckinFilters, value: string | null) => void;
  onSetView: (v: EView) => void;
  order: EOrder;
  orderByKey: string;
  setFilter: (q: string) => void;
  setOrder: (o: EOrder) => void;
  setOrderByKey: (k: string) => void;
  timerFinishedFilter: string | null;
  timerStartedFilter: string | null;
  view: EView;
}

const CompetitionFilterForm = ({
  filter,
  onChange,
  onSetView,
  order,
  orderByKey,
  setFilter,
  setOrder,
  setOrderByKey,
  timerFinishedFilter,
  timerStartedFilter,
  view,
}: Props) => {
  const { t } = useTranslation();

  const sortOptions = [
    { label: t('form.ser'), value: SER },
    { label: t('form.name'), value: NAME },
  ];

  const checkedOptions = [
    { label: t('form.yes'), value: 'true' },
    { label: t('form.no'), value: 'false' },
  ];

  const clearFilterLabel = t('form.clearFilter');

  return (
    <ListFilterToolbar
      popupId="competitionCheckinFilters"
      searchQuery={filter}
      onSearchChange={setFilter}
      hasActiveFilters={
        hasActiveListFilter(timerStartedFilter) || hasActiveListFilter(timerFinishedFilter)
      }
      filterPopoverContent={
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid size={{ sm: 6 }}>
            <FormControl margin="dense" size="small" sx={{ flexDirection: 'column', width: '100%' }}>
              <FilterFieldHeader
                label={t('timers.started')}
                labelId="timerStarted-radio-group"
                showClear={hasActiveListFilter(timerStartedFilter)}
                clearAriaLabel={clearFilterLabel}
                onClear={() => onChange(ECheckinFilters.timerStarted, null)}
              />
              <RadioGroup
                aria-labelledby="timerStarted-radio-group"
                value={timerStartedFilter ?? ''}
                onChange={(_e, v) => onChange(ECheckinFilters.timerStarted, v || null)}
              >
                {checkedOptions.map(c => (
                  <FormControlLabel
                    key={c.value}
                    value={c.value}
                    control={<Radio size="small" />}
                    label={c.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid size={{ sm: 6 }}>
            <FormControl margin="dense" size="small" sx={{ flexDirection: 'column', width: '100%' }}>
              <FilterFieldHeader
                label={t('timers.stopped')}
                labelId="timerFinished-radio-group"
                showClear={hasActiveListFilter(timerFinishedFilter)}
                clearAriaLabel={clearFilterLabel}
                onClear={() => onChange(ECheckinFilters.timerFinished, null)}
              />
              <RadioGroup
                aria-labelledby="timerFinished-radio-group"
                value={timerFinishedFilter ?? ''}
                onChange={(_e, v) => onChange(ECheckinFilters.timerFinished, v || null)}
              >
                {checkedOptions.map(c => (
                  <FormControlLabel
                    key={c.value}
                    value={c.value}
                    control={<Radio size="small" />}
                    label={c.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      }
      order={order}
      orderByKey={orderByKey}
      setOrder={setOrder}
      setOrderByKey={setOrderByKey}
      sortOptions={sortOptions}
      view={view}
      onSetView={onSetView}
    />
  );
};

export default CompetitionFilterForm;
